import { useAppHelpers } from '@/composables/useAppHelpers';
import { doneProgress, startProgress } from '@/composables/useRouteProgress';
import { useToast } from '@/composables/useToast.js';
import router from '@/router';
import { canMutateOnline, getConnectivityStatus } from '@/services/connectivityState.js';
import { onNetworkError, setConnectivityStatus } from '@/services/onlineCheckService.js';
import { useAuthStore } from '@/stores/auth-store';
import { useConfigurationStore } from '@/stores/configuration-store.js';
import axios from 'axios';

axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;

/**
 * Per-purpose request deadlines.
 *
 * `default` stays where it was; the bug was never the length of the timeout, it
 * was that a timeout was read as proof of a dead network (see classifyError).
 * The tiers exist so a 200 MB export or a multi-file upload can say so instead
 * of inheriting a ceiling picked for interactive calls.
 */
const REQUEST_TIMEOUTS = {
    default: 60000,
    probe: 8000,
    report: 120000,
    upload: 300000,
    download: 300000,
};

const apiClient = axios.create({
    baseURL: '/api/',
    // Prevent hung XHRs after sleep / disconnect from holding the top progress bar open.
    timeout: REQUEST_TIMEOUTS.default,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
    },
});

/** Shared in-flight Sanctum CSRF bootstrap so concurrent 419s refresh once. */
let csrfRefreshPromise = null;

function isCsrfBootstrapRequest(config) {
    return String(config?.url || '').includes('/sanctum/csrf-cookie');
}

/**
 * Clear every XSRF duplicate, then fetch one clean replacement.
 *
 * The order is load-bearing and must stay this way round. `deleteCookie` expires
 * the name on the host-only variant *and* on every parent domain — including
 * SESSION_DOMAIN (".inventree.test"), which is the domain the replacement
 * arrives on. Deleting afterwards would therefore destroy the very token this
 * function exists to obtain. Clearing first is what lets the response's
 * Set-Cookie leave exactly one cookie behind, which is the whole point: a stale
 * host-only duplicate is sent alongside the real one, resolves first in PHP, and
 * produces a 419 that no amount of refreshing can recover from.
 *
 * The window this opens — a round trip during which the tab holds no token — is
 * closed by the request interceptor, which parks outbound requests behind
 * `csrfRefreshPromise` rather than letting them depart bare and 419.
 */
async function refreshCsrfCookie() {
    if (!csrfRefreshPromise) {
        csrfRefreshPromise = (async () => {
            useAppHelpers().deleteAllCookies('XSRF-TOKEN');
            await apiClient.get('/sanctum/csrf-cookie', {
                baseURL: '/',
                skipProgress: true,
                retryPolicy: 'none',
            });
        })().finally(() => {
            csrfRefreshPromise = null;
        });
    }
    return csrfRefreshPromise;
}

/**
 * Resolve offlinePolicy for a request.
 * GET/HEAD → read; others → mutation unless explicitly set.
 */
function resolveOfflinePolicy(config) {
    if (config.offlinePolicy) return config.offlinePolicy;
    const method = (config.method || 'get').toLowerCase();
    if (method === 'get' || method === 'head') return 'read';
    return 'mutation';
}

/** Why this write is refused, in the operator's terms rather than the network's. */
const OFFLINE_WRITE_MESSAGES = {
    offline: 'You are offline — changes are disabled until the connection returns.',
    'backend-unavailable': 'The server is temporarily unavailable — please try again in a moment.',
    'expired-session': 'Your session has expired — please sign in again to save changes.',
    checking: 'Still checking the connection — please try again in a moment.',
};

/**
 * The subscription lock, mirrored on the client.
 *
 * The server's BillingGate answers 402 to a mutation while the subscription is
 * locked; this refuses the same request before it departs, so every screen
 * gets one consistent warning through useHttpErrorManager with no per-screen
 * work — the arrangement the offline write gate above already has. The
 * billing, auth and notification endpoints stay open because they are how the
 * lock is lifted, how a user signs out, and how the bell keeps working.
 *
 * Resolved through the store rather than a module-level flag so a 402 body,
 * `/billing/state` and the cached copy all feed one place. A store that cannot
 * be reached (no Pinia yet, as in a bare unit test) reads as "not locked": the
 * server still holds the real gate.
 */
const BILLING_OPEN_PREFIXES = ['billing/', 'auth/', 'notifications/'];
const BILLING_LOCKED_MESSAGE = 'Your subscription is locked — changes are disabled until the payment is made.';

function billingLockedFor(config) {
    let configStore = null;
    try {
        configStore = useConfigurationStore();
    } catch {
        return false;
    }
    if (!configStore?.billingLocked) return false;

    const url = String(config?.url || '')
        .replace(/^\/+/, '')
        .replace(/^api\//, '');
    return !BILLING_OPEN_PREFIXES.some((prefix) => url.startsWith(prefix));
}

function recordBillingState(state) {
    if (!state || typeof state !== 'object') return;
    try {
        useConfigurationStore().setBillingState(state);
    } catch {
        // No store to write to; the caller still receives the 402.
    }
}

function markOnline() {
    // Prefer the service so offline→online listeners (list re-seed) fire.
    const prev = getConnectivityStatus();
    if (prev === 'online') {
        useAuthStore().UPDATE_SYSTEM_ONLINE_STATUS(true);
        return;
    }

    // A 2xx is direct proof the backend is reachable, and — for an authenticated
    // endpoint — that this session is alive. It is the one API outcome allowed
    // to write a verdict rather than merely ask for a probe, because it is the
    // only one that cannot be a false negative.
    setConnectivityStatus('online', { fromEvent: 'api.success' });
}

/*
|--------------------------------------------------------------------------
| Failure taxonomy
|--------------------------------------------------------------------------
|
| Every failure gets a `kind`, and the kind decides two independent questions:
| what it says about connectivity, and whether the request may be replayed.
| Before this existed the interceptor branched on five things and treated the
| rest — every 5xx, every 429 — as a terminal error with no retry and no
| classification, while treating axios's own client-side timeout as proof that
| the network was gone.
|
*/

const RETRY_BASE_MS = 500;
const RETRY_CAP_MS = 8000;
const RETRY_MAX = 3;
/** Hard ceiling on a whole retry chain, so no sequence of failures is unbounded. */
const RETRY_WINDOW_MS = 30000;

function parseRetryAfter(response) {
    const raw = response?.headers?.['retry-after'] ?? response?.headers?.['Retry-After'];
    if (raw == null) return null;
    const seconds = Number(raw);
    if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
    const at = Date.parse(String(raw));
    return Number.isNaN(at) ? null : Math.max(0, at - Date.now());
}

export function classifyError(error) {
    const status = Number(error?.response?.status) || 0;
    const code = error?.code;

    if (code === 'ERR_OFFLINE_WRITE') return { kind: 'blocked-offline', retryable: false };
    if (code === 'ERR_BILLING_LOCKED') return { kind: 'blocked-billing', retryable: false };
    if (code === 'ERR_CANCELED' || error?.name === 'AbortError' || error?.name === 'CanceledError') {
        return { kind: 'cancelled', retryable: false };
    }
    // axios reports its *own* timeout as ECONNABORTED (transitional
    // clarifyTimeoutError is false by default), which is indistinguishable from
    // an aborted request and says nothing whatsoever about the network. A slow
    // upload, a heavy report, or head-of-line queueing behind a fan-out all land
    // here while the server is handling the request perfectly well.
    // Deliberately not retryable. The request already spent its entire timeout
    // budget; replaying it would double or triple the operator's wait for a
    // result that is no more likely the second time. It still asks for a probe,
    // because a timeout is weak evidence — but only evidence.
    if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
        return { kind: 'timeout', retryable: false, signal: 'timeout' };
    }
    if (!error?.response) {
        // No response object at all: the transport genuinely failed.
        return { kind: 'transport', retryable: true, signal: 'transport' };
    }

    if (status === 401) return { kind: 'unauthorized', retryable: false };
    // The subscription lock. Neither a fault nor an outage: the server is
    // reachable and said no, so no probe and no retry — the body carries the
    // state the bar needs and the interceptor records it.
    if (status === 402) return { kind: 'payment-required', retryable: false };
    if (status === 403) return { kind: 'forbidden', retryable: false };
    if (status === 419) return { kind: 'csrf', retryable: false };
    if (status === 422) return { kind: 'validation', retryable: false };
    if (status === 429) {
        return { kind: 'throttled', retryable: true, retryAfterMs: parseRetryAfter(error.response), maxAttempts: 2 };
    }
    if (status === 502 || status === 503 || status === 504) {
        return {
            kind: 'backend-unavailable',
            retryable: true,
            signal: 'backend',
            retryAfterMs: parseRetryAfter(error.response),
        };
    }
    if (status === 500) return { kind: 'server-error', retryable: true, maxAttempts: 1 };
    if (status >= 400 && status < 500) return { kind: 'client-error', retryable: false };
    return { kind: 'unknown', retryable: false };
}

/**
 * May this exact request be sent again?
 *
 * The no-duplicate rule. A bare POST is **never** replayed, whatever the status:
 * the client cannot tell a request the server rejected from one it committed
 * before the connection broke, and guessing wrong bills a customer twice. Only
 * a read, or a write carrying an idempotency key the server enforces, is safe.
 */
function isRetryableRequest(config) {
    if (config?.retryPolicy === 'none') return false;
    const method = String(config?.method || 'get').toLowerCase();
    if (method === 'get' || method === 'head') return true;
    return Boolean(config?.idempotencyKey) || config?.retryPolicy === 'safe';
}

/** Full jitter, so a fleet of terminals recovering together does not resonate. */
function backoffDelay(attempt) {
    return Math.round(Math.random() * Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** attempt));
}

function retryDelayFor(error, classification) {
    const config = error?.config;
    if (!config || !classification.retryable) return null;
    if (!isRetryableRequest(config)) return null;

    const attempt = config._retryCount ?? 0;
    const maxAttempts = classification.maxAttempts ?? RETRY_MAX;
    if (attempt >= maxAttempts) return null;

    const deadline = config._retryDeadline ?? Date.now() + RETRY_WINDOW_MS;
    if (Date.now() >= deadline) return null;

    const delay = classification.retryAfterMs != null ? classification.retryAfterMs : backoffDelay(attempt);
    // Never sleep past the window we promised to stay inside.
    if (Date.now() + delay >= deadline) return null;

    config._retryDeadline = deadline;
    return delay;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Small, slow-moving GETs that half the application asks for on mount.
 *
 * `/branch/all` is requested from eighteen call sites and `getUserPermissions`
 * from seven, almost all of them in `onMounted`. Opening the POS, a transfer, a
 * purchase, a report and a load form in sequence asked five times for the same
 * unchanging list of branches, and a screen with two of these components asked
 * twice before it had finished rendering once.
 *
 * Memoized here rather than in each caller because the callers are not the
 * problem — they are right to want the data, and eighteen edits would be
 * eighteen chances to handle the response differently. This is also the file
 * that already owns request policy: timeouts, retries, the offline gate and the
 * CSRF hold all live in these interceptors.
 *
 * An allowlist, never a heuristic. A blanket GET cache would eventually serve a
 * list endpoint, a report or a balance from memory and be very hard to debug;
 * every entry here is a reference list that changes when an administrator
 * changes it, which is rare and never mid-task. The window is short enough that
 * creating a branch and navigating to a screen that lists it still works.
 *
 * `{ bypassCache: true }` forces a read — for the screen that just wrote one.
 */
const CACHEABLE_GETS = new Map([
    ['/branch/all', 60_000],
    ['branch/all', 60_000],
    ['/user/getUserPermissions', 60_000],
    ['user/getUserPermissions', 60_000],
]);
const getCache = new Map();

function cacheKeyFor(config) {
    const url = String(config.url ?? '');
    if (!CACHEABLE_GETS.has(url)) return null;
    if (String(config.method ?? 'get').toLowerCase() !== 'get') return null;
    if (config.bypassCache) return null;
    // Params change what the answer means, so they change the key.
    const params = config.params ? JSON.stringify(config.params) : '';
    return `${url}?${params}`;
}

/**
 * Drop every memoized answer.
 *
 * Called on logout and on a confirmed expired session: permissions and the
 * branch list are both scoped to who is asking, and serving the previous user's
 * copy to the next one would be a disclosure, not merely a stale read.
 */
export function clearApiGetCache() {
    getCache.clear();
}

apiClient.interceptors.request.use(async function (config) {
    if (config.data instanceof FormData && config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
    }

    if (config.timeoutTier && REQUEST_TIMEOUTS[config.timeoutTier]) {
        config.timeout = REQUEST_TIMEOUTS[config.timeoutTier];
    }

    // An idempotency key travels as a header as well as in the body, so an
    // endpoint can adopt replay resolution without changing its request shape.
    if (config.idempotencyKey && config.headers) {
        config.headers['Idempotency-Key'] = config.idempotencyKey;
    }

    const policy = resolveOfflinePolicy(config);
    config.offlinePolicy = policy;

    if (policy === 'mutation' && !canMutateOnline()) {
        const status = getConnectivityStatus();
        const err = new Error(OFFLINE_WRITE_MESSAGES[status] ?? OFFLINE_WRITE_MESSAGES.offline);
        err.code = 'ERR_OFFLINE_WRITE';
        err.connectivity = status;
        err.config = config;
        err.isAxiosError = true;
        return Promise.reject(err);
    }

    if (policy === 'mutation' && billingLockedFor(config)) {
        const err = new Error(BILLING_LOCKED_MESSAGE);
        err.code = 'ERR_BILLING_LOCKED';
        err.config = config;
        err.isAxiosError = true;
        return Promise.reject(err);
    }

    // Do not depart while the XSRF cookie is being replaced. For the duration of
    // a refresh the jar is deliberately empty (see refreshCsrfCookie), so a
    // request that left now would 419 for certain and consume its one retry
    // recovering from a hole we dug ourselves. The bootstrap request is of
    // course exempt — it is the refresh.
    if (csrfRefreshPromise && !isCsrfBootstrapRequest(config)) {
        await csrfRefreshPromise.catch(() => {});
    }

    // Serve an allowlisted reference GET from memory, or join the one already in
    // flight. Done by swapping the adapter because that is the only point where
    // a request interceptor can answer without departing — and it keeps the rest
    // of the chain (response interceptor, progress, connectivity) unchanged.
    const cacheKey = cacheKeyFor(config);
    if (cacheKey) {
        config._getCacheKey = cacheKey;
        const entry = getCache.get(cacheKey);
        if (entry && (entry.pending || Date.now() - entry.at < CACHEABLE_GETS.get(config.url))) {
            const settled = entry.pending ?? Promise.resolve(entry.response);
            config.adapter = () => settled.then((response) => ({ ...response, config, cached: true }));
        } else {
            // Register the deferred *before* departing, so the second component
            // to mount joins this request rather than starting its own. Two
            // sibling components mounting in the same tick was the common case.
            let resolve;
            let reject;
            const pending = new Promise((res, rej) => {
                resolve = res;
                reject = rej;
            });
            // A joiner that never materialises must not surface as an unhandled
            // rejection; the real failure is reported to the caller that asked.
            pending.catch(() => {});
            getCache.set(cacheKey, { at: 0, response: null, pending, resolve, reject });
        }
    }

    // Background / polling calls can opt out via { skipProgress: true }.
    if (!config.skipProgress) {
        config._progressGeneration = startProgress();
    }
    return config;
});

apiClient.interceptors.response.use(
    async (response) => {
        if (!response.config?.skipProgress && response.config?._progressGeneration !== undefined) {
            doneProgress(response.config._progressGeneration);
        }
        if (response.status >= 200 && response.status < 300) {
            markOnline();
            if (response.config?._getCacheKey && !response.cached) {
                const key = response.config._getCacheKey;
                getCache.get(key)?.resolve?.(response);
                getCache.set(key, { at: Date.now(), response, pending: null });
            }
            return response;
        } else {
            await handleErrorResponse(response);
            return Promise.reject(response);
        }
    },
    async (error) => {
        if (error.config && !error.config.skipProgress && error.config._progressGeneration !== undefined) {
            doneProgress(error.config._progressGeneration);
        }

        const classification = classifyError(error);
        error.kind = classification.kind;

        // A memoized GET that failed must leave nothing behind: the entry is
        // dropped so the next caller retries, and any request that joined this
        // one is failed rather than left waiting on a promise nobody will settle.
        // Done before the retry branches below, because a retried attempt lands
        // back here on its own final failure.
        const failedCacheKey = error.config?._getCacheKey;
        if (failedCacheKey) {
            const entry = getCache.get(failedCacheKey);
            if (entry?.pending) {
                entry.reject?.(error);
                getCache.delete(failedCacheKey);
            }
        }

        if (classification.kind === 'blocked-offline' || classification.kind === 'blocked-billing' || classification.kind === 'cancelled') {
            return Promise.reject(error);
        }

        // A 402 is the server's own copy of the lock. Record the state it
        // carries so the bar and the local gate agree with it from this moment,
        // say so once, and hand the rejection back — nothing to retry, nothing
        // to probe.
        if (classification.kind === 'payment-required') {
            recordBillingState(error.response?.data?.state);
            try {
                useToast().displayToast({
                    message: error.response?.data?.message || BILLING_LOCKED_MESSAGE,
                    variant: 'warning',
                    duration: 8000,
                });
            } catch {
                // No toast store yet; the caller still receives the 402.
            }
            return Promise.reject(error);
        }

        const response = error.response;
        const config = error.config;

        // Concurrent POSTs can race the session CSRF cookie. Refresh once and
        // retry the original request instead of reloading (which looked like logout).
        if (response?.status === 419 && config && !isCsrfBootstrapRequest(config) && !config._csrfRetried) {
            try {
                await refreshCsrfCookie();
                config._csrfRetried = true;
                return apiClient.request(config);
            } catch {
                // Fall through and surface the original 419.
            }
        }

        // Bounded, jittered replay for transient failures. Reads always; writes
        // only when they carry a key the server can deduplicate.
        const delay = retryDelayFor(error, classification);
        if (delay != null) {
            config._retryCount = (config._retryCount ?? 0) + 1;
            console.info('[api] retrying', classification.kind, {
                url: config.url,
                attempt: config._retryCount,
                delay,
            });
            await sleep(delay);
            return apiClient.request(config);
        }

        // Evidence, not a verdict — and only once the request has actually given
        // up. Reporting trouble we are still in the middle of handling would
        // multiply one failure into a probe per attempt. onNetworkError
        // schedules one probe for a whole burst and writes no status of its own,
        // so a slow report or a single dropped packet can no longer refuse every
        // sale in the app.
        if (classification.signal) {
            onNetworkError(classification.signal);
        }

        if (response) {
            await handleErrorResponse(response);
        }
        return Promise.reject(error);
    },
);

/**
 * One 401 is not proof the session ended.
 *
 * `runOfflineListSync` fans many requests out at once, so it is the surface most
 * likely to catch a server-side blip: a request that boots without .env loses
 * APP_URL, drops out of `sanctum.stateful`, and answers 401 while its siblings
 * return 200 (see AppServiceProvider::assertEnvironmentLoaded). Tearing the
 * session down on the first 401 turned that blip into a logout that also wiped
 * the offline cache. Ask /auth/user instead, once per burst — only a 401 there
 * confirms the session is gone. A probe that fails any other way (network,
 * timeout, 5xx) is not a logout.
 */
let sessionProbe = null;

/**
 * Deliberately memoized only while in flight, never cached afterwards.
 *
 * Sharing the in-flight probe is what collapses a burst into one question. A
 * time-based cache on top of it would go further and suppress the *next*
 * question too — which is exactly how a genuine logout gets masked, since the
 * session that answered "alive" a moment ago is precisely the one that just
 * stopped being alive. Dampening a false positive is not worth ignoring a true
 * one.
 */
const sessionIsGone = () => {
    if (sessionProbe) return sessionProbe;

    const probe = apiClient
        .get('/auth/user', {
            skipAuthLogout: true,
            skipProgress: true,
            retryPolicy: 'none',
            timeoutTier: 'probe',
        })
        .then(() => false)
        .catch((error) => Number(error?.response?.status) === 401)
        .finally(() => {
            if (sessionProbe === probe) sessionProbe = null;
        });

    sessionProbe = probe;
    return probe;
};

/**
 * Every 401 in a burst resolves the same probe in the same tick, so they would
 * all reach processLogout together. The store guards itself, but only from the
 * moment its first synchronous statement runs — dedupe here rather than depend
 * on that ordering.
 */
let logoutInFlight = null;

const endSession = (auth) => {
    if (logoutInFlight) return logoutInFlight;

    // Branches and permissions are both scoped to who is asking. Handing the
    // departing user's copy to whoever signs in next would be a disclosure, not
    // just a stale read.
    clearApiGetCache();

    const logout = Promise.resolve(auth.processLogout()).finally(() => {
        if (logoutInFlight === logout) logoutInFlight = null;
    });

    logoutInFlight = logout;
    return logout;
};

const handleErrorResponse = async (response) => {
    if (response && response.status) {
        // API 404s are often expected domain results (for example, searching
        // for an unknown sale). Only page/detail loads that explicitly opt in
        // should replace the current route with the full-page 404 screen.
        if (response.status === 404 && response.config?.redirectOnNotFound === true) {
            await router.push({ name: 'error' });
        }
        if (response.status === 401) {
            const auth = useAuthStore();
            const url = String(response.config?.url || '');
            // Logout itself often returns 401 when the session is already gone.
            // Re-entering processLogout from that response (or while logout is
            // in flight) produces the /auth/user ↔ /auth/logout loop on the
            // login page. Guest session probes must not start a remote logout.
            if (response.config?.skipAuthLogout === true || url.includes('/auth/logout') || auth.processingLogout) {
                return;
            }
            if (!auth.isLoggedIn) {
                auth.CLEAR_USER_DATA();
                return;
            }
            // /auth/user is the confirmation itself — do not probe it with itself.
            if (!url.includes('/auth/user') && !(await sessionIsGone())) {
                return;
            }
            // The session is confirmed gone, and that is neither "offline" nor a
            // backend fault: the honest state says so, and the cache survives it.
            setConnectivityStatus('expired-session', { fromEvent: 'auth.confirmed' });
            // The probe is awaited, so another 401 may have started logout meanwhile.
            if (auth.isLoggedIn && !auth.processingLogout) {
                await endSession(auth);
            }
        }
    } else {
        console.error('Error: ', response);
    }
};

export default apiClient;
